import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import {
  deriveRideStatus,
  expectedJudgeCountForClass,
  expectedSectionCountForClass,
  isPanelConsensusTest,
  resolveScoreSheetTotals,
} from "score-common";
import {
  classes as classesCache,
  getAll,
  horses as horsesCache,
  people as peopleCache,
  rides as ridesCache,
  scoreSheets as scoreSheetsCache,
  tests as testsCache,
} from "../local/db";

// Best score (highest percentage) among the ride's sheets. Needs the test's
// movements to compute; null when the test is unresolved or there are no sheets.
//
// #1891 — resolveScoreSheetTotals (not calculateScoreSheet directly): a
// sheet already confirmed/completed under a since-corrected def must show
// its own frozen percentage here, matching every other surface, rather
// than recomputing against `test` (this hook's live catalog fetch) and
// silently disagreeing with the class grid, the reporting PDF, and FVD.
// An unconfirmed sheet has no frozen maxPoints yet, so this is a no-op for
// the normal in-progress case.
function bestPercentage(sheets, test) {
  if (!test || !sheets.length) return null;
  const best = sheets
    .map((s) => resolveScoreSheetTotals(s, test))
    .sort((a, b) => b.percentage - a.percentage)[0];
  return best?.percentage ?? null;
}

// Denormalize one ride for the ride-card: horse/rider names joined from the
// org's People/Horses, the derived lifecycle status, plus the best-available
// score among the ride's sheets. Manual rides carry their own display names.
function denormRide(ride, test, peopleById, horsesById, sheets, expectedJudgeCount, sectionJudge) {
  // #1763 — a panel-consensus ride (Test.panel.consensus) has ONE sheet
  // co-signed by the whole panel, so counting sheets against the class's
  // staffed judge positions would leave the badge stuck below FINAL for a ride
  // that is fully judged and signed. The Test is already in hand here (it is
  // what bestPercentage scores against), so the flag costs nothing to read.
  const consensus = isPanelConsensusTest(test);
  const status = deriveRideStatus(ride, sheets, expectedJudgeCount, { sectionJudge, consensus });
  const percentage = bestPercentage(sheets, test);
  if (ride.isManual) return { ...ride, status, percentage };
  const horse = horsesById[ride.horseId];
  const rider = peopleById[ride.riderId];
  return {
    ...ride,
    status,
    horseName: horse?.name ?? "—",
    riderName: rider ? `${rider.firstName} ${rider.lastName}` : "—",
    percentage,
  };
}

// Running order: scheduled rides by rideNumber, emergency manual rides last.
function assemble(rows) {
  const real = rows
    .filter((r) => !r.isManual)
    .sort((a, b) => (a.rideNumber ?? 0) - (b.rideNumber ?? 0));
  const manual = rows.filter((r) => r.isManual);
  return [...real, ...manual];
}

const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x]));

const groupSheetsByRide = (sheets) => {
  const map = {};
  for (const s of sheets) (map[s.rideId] ??= []).push(s);
  return map;
};

/**
 * Rides for a class, denormalized for the ride list. Cache-first (#421): built
 * from the IndexedDB caches (rides indexed by classId, plus classes/tests/
 * people/horses/scoreSheets) and returned immediately, then revalidated from
 * score-api when online — the fetch writes every entity back into the cache so
 * the class works offline on the next visit.
 *
 * #203 / #380 / #386 — emergency manual rides live in the same rides cache
 * (isManual, written by addManualRide) and appear here without a network round
 * trip; their ScoreSheet still syncs to score-api via the outbox (#422).
 */
export function useRidesForClass(classId) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  return useCachedResource(
    async () => {
      // #2196 — `undefined` here, not `{ klass: null, rides: [] }`: a caller
      // fed a classId that starts null and resolves later (SessionHeader gets
      // `klass?.id ?? null` from a still-loading parent) must be able to tell
      // "nothing known about this class yet" apart from "loaded, no rides".
      // useCachedResource's own `loading` flag can't carry that distinction
      // across a classId change (#2162: it never resets to true on a dep
      // change), so callers gate on `data === undefined` instead — see
      // SessionHeader's `rideDataKnown`. A real settle (local or remote)
      // never returns `undefined` itself, so the sentinel is unambiguous.
      if (!classId) return undefined;
      const klass = (await classesCache.get(classId)) ?? null;
      const rideRows = await ridesCache.byClass(classId);
      if (!klass && !rideRows.length) return undefined;
      // #435 — test is a property of the RIDE, not the class. Rides in one class
      // often ride DIFFERENT tests; resolve each ride's own test so the ride
      // card's score/percentage uses the right movements. (Never klass.testId.)
      const testsById = {};
      for (const tid of new Set(rideRows.map((r) => r.testId).filter(Boolean))) {
        testsById[tid] = (await testsCache.get(tid)) ?? null;
      }
      const peopleById = byId(await peopleCache.getAll());
      const horsesById = byId(await horsesCache.getAll());
      const sheetsByRide = groupSheetsByRide(await getAll("scoreSheets"));
      // FINAL needs the Class's judge count; null when the cached Class has no
      // judgeAssignments (offline before reconcile) — status degrades to
      // COMPLETED, never falsely FINAL.
      const expectedJudgeCount = expectedJudgeCountForClass(klass);
      const sectionJudge = expectedSectionCountForClass(klass) != null;
      const rows = rideRows.map((r) =>
        denormRide(
          r,
          testsById[r.testId] ?? null,
          peopleById,
          horsesById,
          sheetsByRide[r.id] ?? [],
          expectedJudgeCount,
          sectionJudge,
        ),
      );
      return { klass, rides: assemble(rows) };
    },
    async () => {
      if (!classId) return undefined;

      const [klass, serverRides] = await Promise.all([
        api.classes.getClass(classId).then(unwrap).catch(() => null),
        api.rides.listRides(classId).then(unwrap),
      ]);
      // #435 — resolve each ride's OWN test (ride.testId), not the class's. A
      // class can hold rides on different tests; using the class's single test
      // would render every ride card with the wrong movements/score.
      const testsById = {};
      await Promise.all(
        [...new Set(serverRides.map((r) => r.testId).filter(Boolean))].map(async (tid) => {
          const t = await api.tests.getTest(tid).then(unwrap).catch(() => null);
          testsById[tid] = t;
          if (t) await testsCache.put(t);
        }),
      );
      const [people, horses] = await Promise.all([
        api.people.listPeople().then(unwrap).catch(() => []),
        api.horses.listHorses().then(unwrap).catch(() => []),
      ]);

      // Write everything through to the cache so the class is browsable offline.
      if (klass) await classesCache.put(klass);
      await ridesCache.putAll(serverRides);
      await peopleCache.putAll(people);
      await horsesCache.putAll(horses);

      const peopleById = byId(people);
      const horsesById = byId(horses);

      // #1961 — one batched call for the whole class's rides instead of one
      // listScoreSheets HTTP round trip per ride (the request-shape fix
      // docs/score/query-performance.md ranks this hook against).
      const rideIds = serverRides.map((r) => r.id);
      const allSheets = rideIds.length
        ? await api.scoreSheets
            .listScoreSheetsForRides({ rideIds })
            .then(unwrap)
            .catch(() => [])
        : [];
      const sheetsByRide = groupSheetsByRide(allSheets);
      if (allSheets.length) await scoreSheetsCache.putAll(allSheets);

      const expectedJudgeCount = expectedJudgeCountForClass(klass);
      const sectionJudge = expectedSectionCountForClass(klass) != null;
      const rideRows = serverRides.map((r) =>
        denormRide(
          r,
          testsById[r.testId] ?? null,
          peopleById,
          horsesById,
          sheetsByRide[r.id] ?? [],
          expectedJudgeCount,
          sectionJudge,
        ),
      );
      // Emergency manual rides for this class live in the cache.
      const manualRows = (await ridesCache.byClass(classId))
        .filter((r) => r.isManual)
        .map((r) => ({ ...r, percentage: null }));

      return { klass, rides: assemble([...rideRows, ...manualRows]) };
    },
    [api, classId],
    online,
  );
}
